// eslint-disable-next-line no-restricted-imports
import "react-pdf/dist/Page/AnnotationLayer.css";
// eslint-disable-next-line no-restricted-imports
import "react-pdf/dist/Page/TextLayer.css";

import {
  BottomSheet,
  BottomSheetIllustrationContent,
  LoadingSpinner,
  Text,
  TextSkeleton,
  Title,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { Box, DialogContent, Skeleton, Stack } from "@mui/material";
import { aspectRatio } from "@src/appV2/Agents/components/ContractorBadge/utils/createBadgePdf";
import { APP_V2_APP_EVENTS, useToast } from "@src/appV2/lib";
import { logError } from "@src/appV2/lib/analytics";
import { useCallback, useState } from "react";
import { Document, Page } from "react-pdf";

import { Button } from "../../components/Button";
import { DialogFooter } from "../../components/DialogFooter";
import { IconButton } from "../../components/IconButton";
import { LoadingButton } from "../../components/LoadingButton";
import { getSafeAreaPaddings } from "../../utils/getSafeAreaPaddings";
import { ImageCropperModal, type ImageCropperModalProps } from "./ImageCropperModal";

interface ActionButton {
  label: string;
  disabled?: boolean;
  isLoading?: boolean;
  onClick: () => void;
}

export interface BottomSheetContentProps {
  // We need this to fix this crash: https://github.com/wojtekmaj/react-pdf/issues/974
  pdfKey?: string;
  badgePdf?: Blob;
  modalState: UseModalState;
  workerId?: string;
  isDataLoading: boolean;
  isPdfLoading: boolean;
  isError?: boolean;
  title?: string;
  description?: string;
  primaryButton?: ActionButton;
  secondaryButton?: ActionButton;
  tertiaryButton?: ActionButton;
  imageUploadCropper?: ImageCropperModalProps;
}

export function BottomSheetContent(props: BottomSheetContentProps) {
  const {
    modalState,
    workerId,
    isDataLoading,
    isPdfLoading,
    description,
    badgePdf,
    primaryButton,
    secondaryButton,
    tertiaryButton,
    title,
    isError,
    imageUploadCropper,
    pdfKey,
  } = props;
  const [cardContainerWidth, setCardContainerWidth] = useState(0);
  const { showErrorToast } = useToast();

  // We don't use useRef as on initial render the ref from useRef is not set
  // so we use a callback to set width when the node is actually rendered
  // https://legacy.reactjs.org/docs/hooks-faq.html#how-can-i-measure-a-dom-node
  const cardContainerRef = useCallback((node: HTMLDivElement) => {
    if (node !== null) {
      setCardContainerWidth(node.getBoundingClientRect().width);
    }
  }, []);

  if (imageUploadCropper?.modalState?.modalIsOpen) {
    return <ImageCropperModal {...imageUploadCropper} />;
  }

  if (isError) {
    return (
      <BottomSheet
        modalState={modalState}
        footer={
          <DialogFooter
            orientation="vertical"
            onClose={() => {
              modalState.closeModal();
            }}
          />
        }
      >
        <BottomSheetIllustrationContent
          title="We're having trouble generating your badge"
          description="Please try again in a moment. Contact support if the problem persists."
          illustrationType="error"
        />
      </BottomSheet>
    );
  }

  const isAnyActionButtonLoading =
    Boolean(primaryButton?.isLoading) ||
    Boolean(secondaryButton?.isLoading) ||
    Boolean(tertiaryButton?.isLoading);

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <DialogFooter
          orientation="vertical"
          sx={(theme) => ({
            ...getSafeAreaPaddings({ bottom: true, fallbackBottom: theme.spacing(5) }),
          })}
        >
          {/* Bottom Sheet Content always has a primary button so we reserve the space for it with a loading button */}
          <LoadingButton
            fullWidth
            variant="contained"
            size="large"
            isLoading={isDataLoading || isPdfLoading || Boolean(primaryButton?.isLoading)}
            disabled={isAnyActionButtonLoading}
            onClick={primaryButton?.onClick}
          >
            {primaryButton?.label ?? ""}
          </LoadingButton>
          {secondaryButton && (
            <Button
              fullWidth
              variant="outlined"
              size="large"
              disabled={isAnyActionButtonLoading}
              onClick={secondaryButton.onClick}
            >
              {secondaryButton.label}
            </Button>
          )}
          {tertiaryButton && (
            <Button
              fullWidth
              variant="outlined"
              size="large"
              disabled={isAnyActionButtonLoading}
              onClick={tertiaryButton.onClick}
            >
              {tertiaryButton.label}
            </Button>
          )}
        </DialogFooter>
      }
    >
      <DialogContent sx={{ textAlign: "center" }}>
        <Box display="flex" justifyContent="flex-end">
          <IconButton
            size="small"
            iconType="close"
            variant="outlined"
            aria-label="Close"
            onClick={modalState.closeModal}
          />
        </Box>
        <Box ref={cardContainerRef}>
          <Stack gap={5} alignItems="center">
            {isDataLoading ? (
              <TextSkeleton variant="h3" width="100%" />
            ) : (
              <Title variant="h3" component="h3">
                {title ?? ""}
              </Title>
            )}

            {isDataLoading ? (
              <Stack gap={2} width="100%">
                <Skeleton variant="text" width="100%" height={20} />
                <Skeleton variant="text" width="100%" height={20} />
              </Stack>
            ) : (
              <Text variant="body1">{description}</Text>
            )}

            {isPdfLoading || isDataLoading ? (
              <Skeleton
                variant="rectangular"
                width={cardContainerWidth}
                height={cardContainerWidth / aspectRatio}
              />
            ) : (
              <Box minHeight={cardContainerWidth / aspectRatio}>
                <Document
                  // To fix this crash: https://github.com/wojtekmaj/react-pdf/issues/974
                  key={pdfKey}
                  file={badgePdf}
                  loading={
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      height={cardContainerWidth / aspectRatio}
                    >
                      <LoadingSpinner size="small" variant="primary" />
                    </Box>
                  }
                  onLoadError={(error) => {
                    logError(APP_V2_APP_EVENTS.INDEPENDENT_CONTRACTOR_BADGE_GENERATION_FAILURE, {
                      error,
                      metadata: {
                        workerId,
                      },
                    });

                    showErrorToast(
                      "Failed to load badge. Please try again. If the problem persists, please contact support."
                    );
                  }}
                >
                  <Page
                    pageNumber={1}
                    width={cardContainerWidth}
                    loading={
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        height={cardContainerWidth / aspectRatio}
                      >
                        <LoadingSpinner size="small" variant="primary" />
                      </Box>
                    }
                  />
                </Document>
              </Box>
            )}
          </Stack>
        </Box>
      </DialogContent>
    </BottomSheet>
  );
}
